import React, { ReactElement } from 'react';
import { alpha } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { BaseProps } from 'shared/types/base';
import Box from '@mui/material/Box';
import clsx from 'clsx';

type StyleProps = {
  imageUrl?: string;
};

type DialogTitleWithCloseProps = {
  children?: any;
  actions?: ReactElement | null;
  onClose: () => void;
  imageUrl?: string;
  imageClassName?: string;
  titleVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
} & BaseProps;

const useStyles = makeStyles(theme => ({
  titlePrimary: {
    color: theme.palette.primary.main,
  },
  titleWhite: {
    color: theme.palette.custom.white,
  },
  rootWithImg: {
    height: 250,
    position: 'relative',
  },
  image: ({ imageUrl }: StyleProps) => ({
    background: `url(${imageUrl})`,
    backgroundSize: 'cover',
  }),
  overlay: {
    background: alpha(theme.palette.primary.main, 0.56),
  },
  actions: {
    zIndex: 10,
  },
  whiteIcon: {
    color: theme.palette.custom.white,
  },
}));

const DialogTitleWithClose = React.forwardRef<any, DialogTitleWithCloseProps>(
  (
    { children, actions, onClose, imageUrl, className, imageClassName, titleVariant = 'h5' },
    ref,
  ) => {
    const classes = useStyles({ imageUrl });

    const title = (titleClassName?: string) =>
      children ? (
        <Typography variant={titleVariant} className={titleClassName}>
          <strong>{children}</strong>
        </Typography>
      ) : null;

    return (
      <DialogTitle
        className={className}
        ref={ref}
        classes={{ root: imageUrl ? classes.rootWithImg : undefined }}
      >
        <Grid container justifyContent={children ? 'space-between' : 'flex-end'} alignItems="center">
          {imageUrl ? <div /> : title(classes.titlePrimary)}
          <Grid item className={classes.actions}>
            {actions}
            <IconButton
              aria-label="close"
              edge="end"
              onClick={onClose}
              classes={{ root: imageUrl ? classes.whiteIcon : undefined }}
              size="large">
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
        {imageUrl ? (
          <Box
            position="absolute"
            display="flex"
            alignItems="flex-end"
            left={0}
            top={0}
            width={1}
            height={1}
            className={clsx(classes.image, imageClassName)}
          >
            <Box
              position="absolute"
              left={0}
              top={0}
              width={1}
              height={1}
              className={classes.overlay}
            />
            <Box marginBottom={4} marginLeft={4} zIndex={10}>
              {title(classes.titleWhite)}
            </Box>
          </Box>
        ) : null}
      </DialogTitle>
    );
  },
);

export default DialogTitleWithClose;
